import React, { useEffect, useRef, useState } from 'react';
import { Image, Modal, StyleProp, TextStyle, TouchableOpacity, View, ViewStyle } from 'react-native';
import { faAdd, faFileVideo, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import CustomButton, { ButtonTypes } from './CustomButton';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { WindowInformation } from '../services/window-information';
import { faFilePdf } from '@fortawesome/pro-solid-svg-icons';
import MyAppText from './MyAppText';
import { translate } from '../services/translate';
import FormInput from './FormInput';
import FormActions from './FormActions';

interface FileUploadWithDescriptionProps {
    onSubmit: (description: string, file: File | undefined) => void;
    accept?: string;
    text?: string;
    icon?: IconDefinition;
    type?: ButtonTypes;
    /**
     * button style
     */
    style?: StyleProp<ViewStyle>;
    /**
     * text style
     */
    textStyle?: TextStyle;
    /**
     * button style
    */
    contentContainerStyle?: StyleProp<ViewStyle>;
}

export default function FileUploaderWithDescription({ onSubmit, accept, text, icon, type, style, textStyle, contentContainerStyle }: FileUploadWithDescriptionProps) {
    const windowInfo = WindowInformation();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const { styles, theme } = useStyles(stylesheet);

    const [file, setFile] = useState<{ file: File; objectURI: string; }>();
    const [description, setDescription] = useState<string>('');
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

    function clearValues() {
        setFile(undefined);
        setDescription('');
    }

    async function imageSubmit() {
        try {
            if (onSubmit) {
                onSubmit(description, file?.file);
            }
        } catch (err) {
            console.error(err);
        } finally {
            clearValues();
            setIsModalVisible(false);
        }
    }

    function selectAttachment() {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    }

    useEffect(() => {
        if (isModalVisible) {
            selectAttachment();
        }
    }, [isModalVisible]);

    return (<>
        <Modal
            animationType='fade'
            transparent={true}
            visible={isModalVisible}
            onRequestClose={() => { }}
        >
            <View style={styles.modalContainer}>
                <View style={styles.centeredView}>
                    <View style={[styles.modalBody, windowInfo.isMobile ? { width: '95%' } : { width: '40%' }]}>
                        <MyAppText style={styles.modalTitle}>{translate('addAttachment')}</MyAppText>
                        <View style={[windowInfo.isMobile ? styles.modalContentMobile : styles.modalContent]}>
                            <View style={{ flex: 1 }}>
                                <FormInput
                                    label={translate('description')}
                                    placeholder={translate('description')}
                                    value={description}
                                    onChangeText={setDescription}
                                    multiline={true}
                                    numberOfLines={3}
                                />
                            </View>
                            {file &&
                                <TouchableOpacity onPress={() => selectAttachment()}>
                                    {file.file.type == 'video/mp4' ?
                                        <View key={Math.random()} style={[styles.blockSize, styles.uploadedDefault]}>
                                            <FontAwesomeIcon fontSize={30} icon={faFileVideo} color={theme.colors.iconColor} />
                                        </View>
                                        : file && file.file.type == 'application/pdf' ?
                                            <View key={Math.random()} style={[styles.blockSize, styles.uploadedDefault]}>
                                                <FontAwesomeIcon fontSize={30} icon={faFilePdf as IconDefinition} color={theme.colors.iconColor} />
                                            </View>
                                            : file && <Image
                                                style={[styles.blockSize, {
                                                    overflow: 'hidden',
                                                }]}
                                                source={{
                                                    uri: file.objectURI
                                                }}
                                            />
                                    }
                                </TouchableOpacity>
                            }
                            <TouchableOpacity style={[styles.blockSize, styles.blockStyle, file && { display: 'none' }]} >
                                <label style={styles.addAttachment}>
                                    <input
                                        style={{ display: 'none' }}
                                        type='file'
                                        ref={(input) => (fileInputRef.current = input)}
                                        onChange={async (e) => {
                                            if (!e.target.files) {
                                                return;
                                            }
                                            setFile({ file: e.target.files[0], objectURI: URL.createObjectURL(e.target.files[0]) });
                                        }}
                                        accept={accept}
                                    />
                                    <FontAwesomeIcon fontSize={30} icon={faAdd} color={'#181A1B'} />
                                </label>
                            </TouchableOpacity>
                        </View>
                        <FormActions
                            onSubmit={imageSubmit}
                            onClose={() => {
                                clearValues();
                                setIsModalVisible(false);
                            }}
                            disabled={!file}
                        />
                    </View>
                </View>
            </View>
        </Modal>
        <View style={styles.buttonContainer}>
            <CustomButton text={text} icon={icon} type={type} onPress={() => setIsModalVisible(true)} style={style} textStyle={textStyle} contentContainerStyle={contentContainerStyle} />
        </View>
    </>
    );
}
const stylesheet = createStyleSheet((theme) => ({
    buttonContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-start',
    },
    blockSize: {
        width: 125,
        height: 125,
        borderRadius: 8
    },
    uploadedDefault: {
        backgroundColor: theme.colors.primaryButton.background,
        justifyContent: 'center'
    },
    blockStyle: {
        backgroundColor: '#C6C6CE',
    },
    modalContainer: {
        flex: 1,
        backgroundColor: '#31313199'
    },
    centeredView: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    modalBody: {
        backgroundColor: theme.colors.card.background,
        flexDirection: 'column',
        borderRadius: 8,
        rowGap: 20,
        minWidth: 300,
        minHeight: 300,
        maxHeight: 560,
        padding: 30,
    },
    modalTitle: {
        fontWeight: 'bold',
        fontSize: 16
    },
    modalContent: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        gap: 24,
    },
    modalContentMobile: {
        flexDirection: 'column',
        gap: 24,
    },
    addAttachment: {
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    }
}));

